<template>
    <nav :aria-label="t('navigation.mainNavigation')" class="hidden md:flex flex-col grow min-h-0 w-full min-w-0">
        <div v-if="loadingWorkPanes" class="flex flex-col w-full pt-4" :class="[sidebarPadding, { 'gap-1': !showLabels }]" data-testid="nav-skeleton">
            <template v-for="section in SKELETON_SECTIONS" :key="section.id">
                <div v-if="showLabels && section.title" class="nav-section-title mt-10 mb-1 shrink-0">
                    <SkeletonLoader class="w-20 h-3 my-1 rounded-lg" />
                </div>
                <div v-for="i in section.rows" :key="i" class="nav-item flex items-center shrink-0" :class="{ 'justify-center nav-item-rail': !showLabels }">
                    <SkeletonLoader :class="showLabels ? 'w-3/4 h-3 rounded-lg' : 'w-5 h-3 rounded-lg'" />
                </div>
            </template>
        </div>
        <div v-else class="flex flex-col grow min-h-0 w-full overflow-y-auto pt-4 pb-4 mask-y-from-[calc(100%-1rem)]" :class="[sidebarPadding, { 'gap-1': !showLabels }]">
            <MainMenuRow
                v-for="item in homePanes"
                :key="item.id"
                class="shrink-0"
                :title="item.title"
                :icon="item.icon"
                :href="item.url"
                :data="paneLinkData(item)"
                :test-id="`nav-${item.id}`"
                :current="isCurrentPage(item.url)"
                :show-label="showLabels"
                @click="handlePaneClick(item)" />
            <template v-for="section in sections" :key="section.id">
                <template v-if="section.panes.length">
                    <h3 :class="menuSectionTitleClass()">{{ section.title }}</h3>
                    <MainMenuRow
                        v-for="item in section.panes"
                        :key="item.id"
                        :title="item.title"
                        :icon="item.icon"
                        :href="item.url"
                        :data="paneLinkData(item)"
                        :test-id="`nav-${item.id}`"
                        :aria-label="paneAriaLabel(item)"
                        :current="isCurrentPage(item.url)"
                        :count="paneCount(item.id)"
                        :notified="panesWithNotifications.includes(item.id)"
                        :show-label="showLabels"
                        @click="handlePaneClick(item)" />
                </template>
            </template>
            <template v-if="toolLinks.length || dashboardLink">
                <h3 :class="menuSectionTitleClass()">{{ t("navigation.sections.tools") }}</h3>
                <MainMenuRow
                    v-for="link in toolLinks"
                    :key="link.id"
                    :title="link.title"
                    :icon="link.icon"
                    :href="link.url"
                    :external="link.external"
                    :test-id="`nav-${link.id}`"
                    :current="isCurrentPage(link.url)"
                    :show-label="showLabels"
                    @click="handleToolClick(link)" />
                <MainMenuRow
                    v-if="dashboardLink"
                    :title="dashboardLink.title"
                    :icon="dashboardLink.icon"
                    :href="dashboardLink.url"
                    plain-anchor
                    :current="isCurrentPage(dashboardLink.url)"
                    :show-label="showLabels" />
            </template>
            <template v-if="staffLinks.length || instanceSwitcher">
                <h3 :class="menuSectionTitleClass()">{{ t("navigation.sections.admin") }}</h3>
                <MainMenuRow
                    v-for="link in staffLinks"
                    :key="link.url"
                    :title="link.title"
                    :icon="link.icon"
                    :href="link.url"
                    :plain-anchor="!link.is_inertia"
                    :method="link.method || 'GET'"
                    :current="isCurrentPage(link.url)"
                    :show-label="showLabels" />
                <ValencerInstanceSwitcher v-if="instanceSwitcher" :trigger-class="menuItemClass()" :show-labels="showLabels" />
            </template>
        </div>
    </nav>
</template>

<script lang="ts">
type PaneSection = "home" | "chats" | "about" | "work";

interface PaneData {
    id: MainMenuPane;
    title: string;
    // English, for Amplitude: event properties must not change with the user's language.
    analyticsName: string;
    icon: string;
    url?: string;
    component?: Component;
    section: PaneSection;
    order: number;
}

interface ToolLink {
    id: string;
    title: string;
    analyticsName: string;
    icon: string;
    url: string;
    external?: boolean;
}

export interface Props {
    enabledPanes?: MainMenuPane[];
    panesWithNotifications?: MainMenuPane[];
    paneNotificationCounts?: PaneNotificationCounts;
    loadingWorkPanes?: boolean;
    showLabels?: boolean;
    sidebarPadding?: string;
}
</script>

<script setup lang="ts">
import { usePage } from "@inertiajs/vue3";
import type { DashboardLink, OrganizationDetails, StaffLink, UserDetails } from "~vue/appTypes";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { CHATSOURCE } from "~vue/utils/constants";
import { logUserInteraction } from "~vue/utils/logUtils";
import { type Component, computed, inject, type Ref } from "vue";
import { useI18n } from "vue-i18n";

import SkeletonLoader from "../SkeletonLoader.vue";
import MainMenuRow from "./MainMenuRow.vue";
import { COLLABORATION_PROFILE_BOOTSTRAP_ICON_CLASS, MainMenuPane, paneNotificationAriaLabel, type PaneNotificationCounts } from "./menuPanes";
import ValencerInstanceSwitcher from "./ValencerInstanceSwitcher.vue";

const { t } = useI18n();

type MainMenuPageProps = {
    staff_links: StaffLink[];
    dashboard_link: DashboardLink | null;
};

const organizationDetails = inject<Ref<OrganizationDetails | null>>("organization_details");
const userDetails = inject<Ref<UserDetails | null>>("user_details");
const alignHost = inject<Ref<string>>("align_host");
const page = usePage<MainMenuPageProps>();
const staffLinks = computed<StaffLink[]>(() => page.props.staff_links ?? []);
// Served by the reverse proxy outside Inertia, so these are plain links, not router navigations.
const dashboardLink = computed<DashboardLink | null>(() => page.props.dashboard_link ?? null);
const instanceSwitcher = computed(() => userDetails?.value?.valencer_instance_switcher ?? null);

const emit = defineEmits(["openPane"]);

const props = withDefaults(defineProps<Props>(), {
    enabledPanes: () => [],
    panesWithNotifications: () => [],
    paneNotificationCounts: () => ({}),
    loadingWorkPanes: false,
    showLabels: true,
    sidebarPadding: "",
});

function paneCount(id: MainMenuPane): number {
    return props.paneNotificationCounts[id] ?? 0;
}

function paneAriaLabel(item: PaneData): string {
    if (item.id === MainMenuPane.GUIDED_SETUP && props.panesWithNotifications.includes(item.id)) {
        return t("navigation.a11y.paneNew", { title: item.title });
    }
    return paneNotificationAriaLabel(item.id, item.title, paneCount(item.id));
}

function paneLinkData(item: PaneData): Record<string, unknown> | undefined {
    return item.url ? { chat_source: CHATSOURCE.LEFT_SIDEBAR } : undefined;
}

type PaneDefinition = Omit<PaneData, "title"> & { title: () => string };

const PANE_DATA: Record<MainMenuPane, PaneDefinition> = {
    [MainMenuPane.HOME]: {
        id: MainMenuPane.HOME,
        analyticsName: "Home",
        title: () => t("navigation.panes.home"),
        icon: "bi-house",
        url: "/",
        section: "home",
        order: 1,
    },
    [MainMenuPane.NEW_CHAT]: {
        id: MainMenuPane.NEW_CHAT,
        analyticsName: "New chat",
        title: () => t("navigation.panes.newChat"),
        icon: "bi-plus-circle",
        url: "/coach/start/",
        section: "chats",
        order: 2,
    },
    [MainMenuPane.HISTORY]: {
        id: MainMenuPane.HISTORY,
        analyticsName: "Chat history",
        title: () => t("navigation.panes.chatHistory"),
        icon: "bi-chat",
        url: "/past-conversations",
        section: "chats",
        order: 3,
    },
    [MainMenuPane.GUIDED_SETUP]: {
        id: MainMenuPane.GUIDED_SETUP,
        analyticsName: "Coaching setup",
        title: () => t("navigation.panes.coachingSetup"),
        icon: "bi-stars",
        url: "/onboarding/calendar-first/setup/",
        section: "about",
        order: 4,
    },
    [MainMenuPane.PROFILE]: {
        id: MainMenuPane.PROFILE,
        analyticsName: "Private profile",
        title: () => t("navigation.panes.privateProfile"),
        icon: "bi-person-circle",
        url: "/accounts/user-profile",
        section: "about",
        order: 5,
    },
    [MainMenuPane.COLLABORATION_PROFILE]: {
        id: MainMenuPane.COLLABORATION_PROFILE,
        analyticsName: "Collaboration profile",
        title: () => t("navigation.panes.collaborationProfile"),
        icon: COLLABORATION_PROFILE_BOOTSTRAP_ICON_CLASS,
        url: "/accounts/collaboration-profile",
        section: "about",
        order: 6,
    },
    [MainMenuPane.MY_WORK]: {
        id: MainMenuPane.MY_WORK,
        analyticsName: "My work insights",
        title: () => t("navigation.panes.myWorkInsights"),
        icon: "bi-lightbulb",
        url: "/intelligence/my-work/",
        section: "about",
        order: 7,
    },
    [MainMenuPane.CONNECTIONS]: {
        id: MainMenuPane.CONNECTIONS,
        analyticsName: "People",
        title: () => t("navigation.panes.people"),
        icon: "bi-people",
        url: "/people/",
        section: "work",
        order: 8,
    },
    [MainMenuPane.JOURNEYS]: {
        id: MainMenuPane.JOURNEYS,
        analyticsName: "Journeys",
        title: () => t("navigation.panes.journeys"),
        icon: "bi-flag",
        url: "/journeys/",
        section: "work",
        order: 9,
    },
    [MainMenuPane.DEVELOPMENT_JOURNEYS]: {
        id: MainMenuPane.DEVELOPMENT_JOURNEYS,
        analyticsName: "Development journeys",
        title: () => t("navigation.panes.developmentJourneys"),
        icon: "bi-signpost-split",
        url: "/development-journeys/",
        section: "work",
        order: 10,
    },
    [MainMenuPane.PERFORMANCE_MANAGEMENT]: {
        id: MainMenuPane.PERFORMANCE_MANAGEMENT,
        analyticsName: "Performance management",
        title: () => t("navigation.panes.performanceManagement"),
        icon: "bi-graph-up",
        url: "/performance_management",
        section: "work",
        order: 11,
    },
    [MainMenuPane.COMPETENCY_ASSESSMENT]: {
        id: MainMenuPane.COMPETENCY_ASSESSMENT,
        analyticsName: "Competency assessment",
        title: () => t("navigation.panes.competencyAssessment"),
        icon: "bi-trophy",
        url: "/competency-assessment/",
        section: "work",
        order: 12,
    },
    [MainMenuPane.SCHNEIDER_ASSESSMENT]: {
        id: MainMenuPane.SCHNEIDER_ASSESSMENT,
        analyticsName: "IMPACT Leader Code Self-Reflection",
        title: () => t("navigation.panes.impactLeaderCode"),
        icon: "bi-patch-check",
        url: "/schneider-assessment/",
        section: "work",
        order: 13,
    },
    [MainMenuPane.REFLECTIONS]: {
        id: MainMenuPane.REFLECTIONS,
        analyticsName: "Reflections",
        title: () => t("navigation.panes.reflections"),
        icon: "bi-pencil",
        url: "/reflections",
        section: "work",
        order: 14,
    },
};

const SECTION_TITLES: Record<Exclude<PaneSection, "home">, () => string> = {
    chats: () => t("navigation.sections.chats"),
    about: () => t("navigation.sections.about"),
    work: () => t("navigation.sections.work"),
};

const panes = computed(() => {
    return props.enabledPanes
        .map((p) => {
            const pane: PaneData = { ...PANE_DATA[p], title: PANE_DATA[p].title() };
            if (p === MainMenuPane.PERFORMANCE_MANAGEMENT) {
                const customLabel = organizationDetails?.value?.perf_review_custom_copy?.page_tab_label;
                if (customLabel) {
                    pane.title = customLabel;
                    pane.analyticsName = customLabel;
                }
            }
            if (p === MainMenuPane.PROFILE && !props.enabledPanes.includes(MainMenuPane.COLLABORATION_PROFILE)) {
                pane.title = t("navigation.panes.profile");
                pane.analyticsName = "Profile";
            }
            return pane;
        })
        .sort((a, b) => a.order - b.order);
});

const SKELETON_SECTIONS = [
    { id: "home", title: false, rows: 1 },
    { id: "work", title: true, rows: 4 },
    { id: "you", title: true, rows: 3 },
];

const homePanes = computed(() => panes.value.filter((p) => p.section === "home"));

const sections = computed(() =>
    (Object.keys(SECTION_TITLES) as Exclude<PaneSection, "home">[]).map((id) => ({
        id,
        title: SECTION_TITLES[id](),
        panes: panes.value.filter((p) => p.section === id),
    })),
);

const toolLinks = computed<ToolLink[]>(() => {
    const user = userDetails?.value;
    const links: ToolLink[] = [];
    if (user?.is_customer_demo_accounts_enabled) {
        links.push({ id: "demo-accounts", title: t("navigation.tools.demoAccounts"), analyticsName: "Demo Accounts", icon: "bi-person-badge", url: "/demo-accounts/" });
    }
    if (user?.can_see_client_analytics) {
        links.push({ id: "analytics", title: t("navigation.tools.analytics"), analyticsName: "Analytics", icon: "bi-graph-up", url: "/client-analytics/" });
    }
    if (organizationDetails?.value?.team_tools_link_enabled && alignHost?.value) {
        links.push({
            id: "team-tools",
            title: t("navigation.tools.teamTools"),
            analyticsName: "Valence Team Tools",
            icon: "bi-box-arrow-up-right",
            url: alignHost.value,
            external: true,
        });
    }
    if (user?.has_workiq_connected) {
        links.push({ id: "workiq-insights", title: t("navigation.tools.workIqDemo"), analyticsName: "Work IQ (Demo)", icon: "bi-stars", url: "/workiq/insights/" });
    }
    return links;
});

function isCurrentPage(url?: string): boolean {
    if (!url) {
        return false;
    }

    const currentPath = page.url.split("?")[0].replace(/\/$/, "") || "/";
    const targetPath = url.split("?")[0].replace(/\/$/, "") || "/";
    return targetPath === "/" ? currentPath === "/" : currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
}

const handlePaneClick = (item: PaneData) => {
    logUserInteraction(AmplitudeEventNames.MAIN_MENU_ITEM_CLICKED, { id: item.id, name: item.analyticsName, url: item.url, icon: item.icon });

    if (item.id === MainMenuPane.PERFORMANCE_MANAGEMENT) {
        logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_ENTRY_CLICKED, { entry_source: "side_nav" });
    }

    if (!item.url) {
        emit("openPane", item);
    }
};

const handleToolClick = (link: ToolLink) => {
    logUserInteraction(AmplitudeEventNames.MAIN_MENU_ITEM_CLICKED, { id: link.id, title: link.analyticsName });
};

function menuItemClass(): string {
    return ["menu-item nav-item flex items-center gap-3 min-w-0", props.showLabels ? "w-full" : "justify-center nav-item-rail w-auto self-center"].join(" ");
}

function menuSectionTitleClass(): string {
    return props.showLabels ? "scaffold-details-small uppercase text-nav-section-title truncate shrink-0 nav-section-title mt-10 mb-1" : "hidden";
}
</script>

<!-- .menu-item interaction styles live in tailwind.css so rows rendered by
     other components (e.g. ValencerInstanceSwitcher's trigger) get them too. -->
